import { ChapterModel } from '../../models/chapter.model';
import { StoryChapterThumbnail } from '../../models/story-chapter-thumbnail.model';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { ScreenDimensions } from 'src/app/shared/models/screen.dimensions.model';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { ImageTypeEnum } from 'src/app/shared/models/image-type.enum';
import { ClipDirection, ClippingPlane } from '../../models/clipping-plane.model';

import { ChapterUIModel } from '../../models/chapter-ui.model';
import { PostProcessingEffect } from 'src/app/engine/models/post-processing/effects';
import { TransitionModel } from '../../models/transition.model';
import { TransparencyModeEnum } from 'src/app/engine/models/post-processing/transparency-mode.enum';
import { ChapterLegendModel } from '../../models/chapter-legend.model';
import { Patch } from 'src/vendor/immer-6.0.9/compat/pre-3.7/dist/immer';

import { VisibilityLayerModel } from 'src/app/story/models/visibility-layers.model';
import { ContentBlock } from 'src/app/content-blocks/models/content-block';

export class ApplyChapterPatch {
  static readonly type = '[Story Chapter] Apply Patch';
  constructor(public patches: Patch[]) {}
}

export class NextChapter {
  static readonly type = '[Story Chapter] Next Chapter';
  constructor() { }
}

export class PreviousChapter {
  static readonly type = '[Story Chapter] Previous Chapter';
  constructor() { }
}

export class FirstChapter {
  static readonly type = '[Story Chapter] First Chapter';
  constructor() { }
}

export class LastChapter {
  static readonly type = '[Story Chapter] Last Chapter';
  constructor() { }
}

export class SelectChapter {
  static readonly type = '[Story Chapter] Select Chapter';
  constructor(public chapter?: ChapterModel) { }
}

export class SelectChapterById {
  static readonly type = '[Story Chapter] Select Chapter by chapter id';
  constructor(public chapterId: string) { }
}

export class SetChapters {
  static readonly type = '[Story Chapter] Set Chapters';
  constructor(public chapters: ChapterModel[]) { }
}

export class SetChapterCameraAction {
  static readonly type = '[Story Chapter] Set Chapter Camera Action';
  constructor(public chapterId: string, public cameraAction: 'jumpTo' | 'flyTo') { }
}

export class SetChapterUI {
  static readonly type = '[Story Chapter] Set Chapter UI';
  constructor(public chapterId: string, public ui: ChapterUIModel) { }
}

export class SetChapterInfo {
  static readonly type = '[Story Chapter] Set Chapter Info';
  constructor(public chapterId: string, public info: ChapterModel['info']) { }
}

export class SetChapterLegend {
  static readonly type = '[Story Chapter] Set Chapter Legend';
  constructor(public chapterId: string, public legend: ChapterModel['legend']) { }
}

export class AddToChapterLegend {
  static readonly type = '[Story Chapter] Add To Chapter Legend';
  constructor(public chapterId: string, public legend: ChapterLegendModel) { }
}

export class ReorderChapters {
  static readonly type = '[Story Chapter] Reorder Chapters';
  constructor(public previousIndex: number, public currentIndex: number) { }
}

export class SetChapterReadyState {
  static readonly type = '[Story Chapter] Set Readystate';
  constructor(public chapterId: string, public readyState: {model?: boolean, animation?: boolean}) { }
}


export class SetChapterPostProcessing {
  static readonly type = '[Story Chapter] Set Chapter PostProcessing';
  constructor(public chapterId: string, public effects: PostProcessingEffect[], public properties: any) { }
}

export class SetChapterTransitions {
  static readonly type = '[Story Chapter] Set Chapter Transitions';
  constructor(public chapterId: string, public transition: TransitionModel, public key: 'pre' | 'post') { }
}

export class SetChapterTransparencyMode {
  static readonly type = '[Story Chapter] Set Chapter Transparency Mode';
  constructor(public chapterId: string, public transparencyMode: TransparencyModeEnum) {}
}

export class SetChapterIds {
  static readonly type = '[Story Chapter] Set Chapters Ids';
  constructor(public chapterIds: string[]) { }
}

export class SetChapterClippingPlane {
  static readonly type = '[Story Chapter] Set Chapter Clipping Planes';
  constructor(
    public chapterId: string,
    public clips: Record<ClipDirection, ClippingPlane>,
    public enabled: boolean,
    public record: boolean
  ) {}
}

export class SetChapterBackground {
  static readonly type = '[Story Chapter] Set Chapter Background';
  constructor(public chapterId: string, public background: BackgroundModel) { }
}

export class SetAllChaptersBackground {
  static readonly type = '[Story Chapter] Set All Chapters Background';
  constructor(public background: BackgroundModel) { }
}

export class ResetSelectedChapterBackground {
  static readonly type = '[Story Chapter] Reset Selected Chapter Background';
  constructor() { }
}

export class SetSelectedChapterBackground {
  static readonly type = '[Story Chapter] Set Selected Chapter Background';
  constructor(public background: BackgroundModel) { }
}


export class DuplicateChapter {
  static readonly type = '[Story Chapter] Duplicate An Existing Chapter';
  constructor(public chapter: ChapterModel, public chapterId: string) {}
}

export class RemoveChapter {
  static readonly type = '[Story Chapter] Remove Chapter';
  constructor(public chapter: ChapterModel) { }
}

export class ResetStoryChapters {
  static readonly type = '[Story Chapter] Reset';
  constructor() { }
}


export class RegisterChapterThumbnails {
  static readonly type = '[Story Chapters] Register thumbnail URLs from story';
  constructor(public thumbnails: Map<string, StoryChapterThumbnail>) {}
}


export class CopyThumbnail {
  static readonly type = '[Story Chapters] Copy Thumbnail From Chapter';
  constructor(public sourceChapter: ChapterModel, public targetChapter: ChapterModel) {}
}

export class TakeChapterPicture {
  static readonly type = '[Story Chapters] Take picture';
  constructor(
    public chapterId: string,
    public imageType = ImageTypeEnum.JPEG,
    public imageQuality = 0.92,
    public dimensions?: ScreenDimensions,
    public crop: 'original' | 'square' = 'original',
    public includeLabels?: boolean, 
    public includeCopyright?: boolean,
    public layers: HTMLCanvasElement[] = []
  ) {
  }
}

export class TakeChapterPictureSuccess {
  static readonly type = '[Story Chapters] Take picture success';
  constructor(public chapterId: string, public blob: Blob, public dimensions: ScreenDimensions) {}
}

export class RecordCamera {
  static readonly type = '[Story Chapters] Record Camera';
  constructor(public chapterId: string, public camera: CameraModel) {}
}

export class SetChapterCurrentLayers {
  static readonly type = '[Story Chapters] Set the current visibility layers';
  constructor(
    public chapterId: string, 
    public layers: VisibilityLayerModel[]
  ) {}
}

export class SetChapterCurrentLayersIndexPosition {
  static readonly type = '[Story Chapters] Set the current visibility layers index and position';
  constructor(
    public chapterId: string, 
    public index: number, 
    public position: number,
    public record: boolean
  ) {}
}

export class SetChapterMedia {
  static readonly type = '[Story Chapters] Set the media object';
  constructor(
    public chapterId: string, 
    public media: object
  ) {}
}

export class SetChapterContentBlocks {
  static readonly type = '[Story Chapters] Set the content blocks';
  constructor(
    public chapterId: string, 
    public contentBlocks: ContentBlock[]
  ) {}
}
